import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { LuMapPin, LuArchive, LuTrash2, LuPencil, LuPlus } from 'react-icons/lu';
import ModalShell from './ui/ModalShell';
import ConfirmDialog from './ui/ConfirmDialog';
import SedeForm from './cliente/SedeForm';
import { irA } from '../utils/navegacion';

// Lugares (sedes) de un cliente. 12-oct-2026: se pueden editar (antes solo alta, archivar y borrar),
// pasa a ModalShell (no se cortaba el título ni quedaba media pantalla vacía) y confirma con
// ConfirmDialog en vez de window.confirm.
const mensajeError = (err, defecto) => {
    const d = err?.response?.data;
    return typeof d === 'string' ? d : d?.mensaje || d?.message || defecto;
};

export default function SedeModal({ cliente, sedes: sedesIniciales, onRefresh, onClose }) {
    // Se piden acá (activas y con sus equipos): la lista general traía también las archivadas
    const [sedes, setSedes] = useState(() => (sedesIniciales || []).filter(s => s.activa !== false));
    const cargar = useCallback(() => api.get(`/sedes/cliente/${cliente.id}`)
        .then(r => setSedes(Array.isArray(r.data) ? r.data : [])).catch(() => {}), [cliente.id]);
    useEffect(() => { cargar(); }, [cargar]);
    const refrescar = () => { cargar(); onRefresh && onRefresh(); };
    const [editando, setEditando] = useState(null);   // null | 'nueva' | sede
    const [confirmar, setConfirmar] = useState(null); // { tipo: 'archivar' | 'borrar' | 'borrar2', sede }

    const archivar = async (sede) => {
        const t = toast.loading('Archivando…');
        try {
            await api.delete(`/sedes/${sede.id}`);
            toast.success('Lugar archivado', { id: t });
            refrescar();
        } catch (err) { toast.error(mensajeError(err, 'No se pudo archivar'), { id: t }); }
    };
    const borrar = async (sede) => {
        const t = toast.loading('Eliminando…');
        try {
            await api.delete(`/sedes/${sede.id}/definitivo`);
            toast.success('Lugar eliminado', { id: t });
            refrescar();
        } catch (err) { toast.error(mensajeError(err, 'No se pudo eliminar'), { id: t }); }
    };
    const guardar = async (datos) => {
        const esNueva = editando === 'nueva';
        try {
            if (esNueva) await api.post('/sedes', { ...datos, clienteId: cliente.id });
            else await api.put(`/sedes/${editando.id}`, datos);
            toast.success(esNueva ? 'Lugar agregado' : 'Lugar actualizado');
            setEditando(null);
            refrescar();
            return true;
        } catch (err) {
            toast.error(mensajeError(err, 'No se pudo guardar'));
            return false;
        }
    };

    const n = sedes.length;
    const icono = 'w-9 h-9 rounded-xl flex items-center justify-center active:scale-90 transition-all';

    return (
        <>
            <ModalShell titulo={`Lugares · ${cliente.nombre}`}
                subtitulo={`${n} lugar${n !== 1 ? 'es' : ''} registrado${n !== 1 ? 's' : ''}`}
                onCerrar={onClose}
                pie={editando ? null : (
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="flex-1 h-11 rounded-xl bg-chip text-secondary text-label font-black active:scale-95">Cerrar</button>
                        <button type="button" onClick={() => setEditando('nueva')}
                            className="flex-[2] h-11 rounded-xl bg-brand-red text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                            <LuPlus size={15} /> Nuevo lugar
                        </button>
                    </div>
                )}>
                {editando ? (
                    <SedeForm inicial={editando === 'nueva' ? null : editando} onGuardar={guardar} onCancelar={() => setEditando(null)} />
                ) : n === 0 ? (
                    <p className="py-10 text-center text-caption text-muted border-2 border-dashed border-black/[0.07] dark:border-white/[0.07] rounded-2xl">
                        Sin lugares cargados
                    </p>
                ) : (
                    <div className="space-y-2.5">
                        {sedes.map(s => (
                            <div key={s.id} className="p-3.5 bg-panel rounded-2xl border border-black/[0.07] dark:border-white/[0.07]">
                                <div className="flex justify-between items-start gap-3">
                                    <button type="button" onClick={() => setEditando(s)} className="flex-1 min-w-0 text-left">
                                        <p className="font-black text-body text-ink leading-tight">{s.nombreSede}</p>
                                        <p className="text-caption text-muted truncate">
                                            {[s.direccion, s.localidad && !String(s.direccion || '').includes(s.localidad) ? s.localidad : null].filter(Boolean).join(' · ') || 'Sin dirección'}
                                        </p>
                                        {s.equipos?.length > 0 && (
                                            <p className="text-label font-black text-brand-amber mt-0.5">{s.equipos.length} equipo{s.equipos.length !== 1 ? 's' : ''}</p>
                                        )}
                                        {s.notas && <p className="text-caption text-secondary mt-0.5 line-clamp-2">{s.notas}</p>}
                                    </button>
                                    <div className="flex gap-1.5 shrink-0">
                                        <button type="button" onClick={() => setEditando(s)} title="Editar" aria-label="Editar"
                                            className={`${icono} bg-chip text-ink`}><LuPencil size={15} /></button>
                                        {s.direccion && (
                                            <button type="button" title="Ver en el mapa" aria-label="Ver en el mapa"
                                                onClick={() => irA({ direccion: `${s.direccion}, ${s.provincia || 'Buenos Aires'}, Argentina`, nombre: s.nombreSede })}
                                                className={`${icono} bg-chip text-ink`}><LuMapPin size={15} /></button>
                                        )}
                                        <button type="button" onClick={() => setConfirmar({ tipo: 'archivar', sede: s })} title="Archivar (conserva el historial)" aria-label="Archivar"
                                            className={`${icono} bg-[#D48800]/10 dark:bg-[#F0A500]/10 text-brand-amber`}><LuArchive size={15} /></button>
                                        <button type="button" onClick={() => setConfirmar({ tipo: 'borrar', sede: s })} title="Eliminar definitivamente" aria-label="Eliminar"
                                            className={`${icono} bg-[#D13A28]/10 dark:bg-[#E8422F]/10 text-brand-red`}><LuTrash2 size={15} /></button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </ModalShell>

            {confirmar?.tipo === 'archivar' && (
                <ConfirmDialog titulo={`¿Archivar "${confirmar.sede.nombreSede}"?`}
                    mensaje={`${confirmar.sede.equipos?.length ? `Tiene ${confirmar.sede.equipos.length} equipo(s). ` : ''}Deja de aparecer, pero el historial se conserva.`}
                    textoConfirmar="Sí, archivar" onCancelar={() => setConfirmar(null)}
                    onConfirmar={() => { const s = confirmar.sede; setConfirmar(null); archivar(s); }} />
            )}
            {confirmar?.tipo === 'borrar' && (
                <ConfirmDialog titulo={`¿Eliminar "${confirmar.sede.nombreSede}" para siempre?`}
                    mensaje={`Se borran ${confirmar.sede.equipos?.length ? `sus ${confirmar.sede.equipos.length} equipo(s) y ` : ''}todos sus trabajos. No se puede deshacer. Si solo querés sacarlo de la lista, archivalo.`}
                    textoConfirmar="Sí, eliminar" onCancelar={() => setConfirmar(null)}
                    onConfirmar={() => setConfirmar({ tipo: 'borrar2', sede: confirmar.sede })} />
            )}
            {confirmar?.tipo === 'borrar2' && (
                <ConfirmDialog titulo="Segunda confirmación" mensaje={`Se elimina "${confirmar.sede.nombreSede}" con todo su historial.`}
                    textoConfirmar="Eliminar definitivamente" onCancelar={() => setConfirmar(null)}
                    onConfirmar={() => { const s = confirmar.sede; setConfirmar(null); borrar(s); }} />
            )}
        </>
    );
}
